// vue
import { ref, computed, reactive, watch, onMounted, onUnmounted } from 'vue';
import moment from 'moment';

// services
import BotsService from'@services/bots';
import StatsService from '@services/statsService';
import LocalStorage from '@services/ts/localStorage';

// shared
import { CRUD_PAGER_FACTORY } from '@shared/factories';

// i18n
import { useI18n } from 'vue-i18n';

// router
import { useRoute, useRouter } from 'vue-router';

// UI
import { useMessage, useNotification } from 'naive-ui';

// WsOrders
import WsOrders from '@services/trade-statistic/ws-orders';

// shared
import conf from '@shared/conf';

// composables
import { usePNL } from './composables/usePNL';
import { useWsOrders } from './composables/useWsOrders';
import { useWsLastCycle } from './composables/useWsLastCycle';
import { useTvOrders } from './composables/useTvOrders';

// store
import { useGl } from '@store/ts/gl';
import { useEnv } from '@store/ts/env';
import { useRefs } from '@store/ts/refs';
import { useStats } from '@store/stats';
import { useConfirm } from '@store/confirm';

// icons
import {
    Edit20Filled,
    Play20Regular,
    Stop20Regular,
    ArrowSync12Regular,
    PauseCircle24Regular } from '@vicons/fluent';

export default function (props, context) {
    // store
    const gl = useGl();
    const env = useEnv();
    const refs = useRefs();
    const stats = useStats();
    const confirm = useConfirm();
    
    const notification = useNotification();

    const route = useRoute();
    const router = useRouter();
    const intervalId = ref(null);

    // i18n
    const { t } = useI18n();

    // vars
    const { _ } = window;
    const botShortInfo = ref();
    const init = ref(false);
    const totalUpdateLoading = ref(false);
    const pairOFCurrentCycle = ref();
    const showMoreButtons = ref(false);
    const oldCurrentCycle = ref();
    const lastCycleInFlight = ref(false);

    const confirmLabel = ref();
    const declineMessage = ref();
    const genConfCheck = ref(false);
    const markLastAsCanceled = ref(false);
    const defaultCycleId = ref(route.params?.cycle_id);
    const RE_SET_FIX_ORDER_FACTORY = id => ({
        rate_to_finish_cycle: 0,
        rate_to_get_profit_hint: '',
        rate_to_get_profit: '',
        new_rate_to_finish_cycle: '',
        new_rate_to_get_profit_hint: '0%',
        new_rate_to_finish_cycle_suggest: 'Please, calculate',
    
        stop_bot_after_fix_completed: false,
    });
    const reSetFixOrder = ref(RE_SET_FIX_ORDER_FACTORY());

    // useWsOrders
    const {
        useConnect,
        useDisconnect,
    } = useWsOrders();

    const {
        useWsLastCycleConnect,
        useWsLastCycleDisconnect,
    } = useWsLastCycle();

    // useTvOrders
    const {
        chartModel,
        clearTvOrders,
        usePrepareTvOrders,
        clearAllTvOrders,
    } = useTvOrders();

    const loadings = reactive({
        table: false,
        clearCycles: false,
        tableOrders: false,
    });
    
    const filters = reactive(CRUD_PAGER_FACTORY({
        bots: [+route.params.id],
        period: moment().subtract(60, 'days').format('YYYY-MM-DD') + ' ' + moment().add(7, 'days').format('YYYY-MM-DD'),
        statuses: [-1],
    }, {
        page: 1,
        perPage: window.isValidJSON(LocalStorage.getItem('__filters_cycles_table'))
            ? JSON.parse(LocalStorage.getItem('__filters_cycles_table'))
            : 10,
    }));

    const filtersChanged = computed(() => {
        return filters.filters.statuses[0] !== -1;
    });

    const resetFilters = () => {
        filters.filters.statuses = [ -1 ];
            
        cyclesGet();
    };

    const refFilters = computed(() => [
        {
            setAll: false,
            mode: 'radio_group',
            title: stats.localization['trade_statistics_filters_statuses'],
            refs: stats.cycleFilters,
            value: filters.filters.statuses[0],
            filter: 'filters.statuses',
            seeable: filters.filters.statuses[0] != -1,
        }, {
            type: 'render',
            mode: 'date_picker',
            value: periods.value,
            filter: 'filters.period',
        },
    ]);
    const numberOfDecimalPlaces = computed(() => +(String(botShortInfo.value?.buy).split('.')[1]?.length));

    // алгоритм бота
    const isLong = computed(() => botShortInfo.value?.algo == 'long');

    // текст состояния
    const stateText = computed(() => {
        const trimmedStr = botShortInfo.value?.state_txt.trimEnd();
        if (trimmedStr) {
            const last = trimmedStr[trimmedStr.length - 1];
            const preLast = trimmedStr[trimmedStr.length - 2];
            
            if (last == ')' && preLast == '(') {
                return trimmedStr.slice(0, -2);
            }
            return trimmedStr;
        } else {
            return '';
        }
    });

    // текщий цикл
    const currentCycleInfo = computed(() => stats.cyclesDataTable?.records.find(({ number_id }) => number_id == stats.currentCycle));

    // пара текущего цикла
    const currentCyclePair = computed(() => currentCycleInfo.value?.pair);

    // активный цикл
    const activeCycle = computed(() => stats.cyclesDataTable?.records.find(({ status }) => status == 0));

    // текущий цикл - активный цикл
    const currentCycleIsActiveId = computed(
        () => stats.cyclesDataTable?.records.findIndex(({ status, number_id }) => {
            return number_id == stats.currentCycle && status == 0;
        }));

    // текущий пнл из-таблицы
    const currentCycleIsActiveData = computed(() => stats.cyclesDataTable?.records[currentCycleIsActiveId.value]);

    // watch(currentCycleIsActiveId, idx => {
    //     if (~idx) {
    //         if (init.value) {
    //             wsOrdersConnect(idx);
    //         }
    //     } else {
    //         useDisconnect();
    //     }
    // });

    const wsOrdersConnect = idx => {
        const botId = route.params.id;
        const cycleId = stats.cyclesDataTable?.records[idx].number_id;
        
        const callBack = ($event) => {
            const cycle = $event?.cycle;
            const orders = $event?.orders?.filter(({ status }) => [0, 1].includes(status));
                    
            if (cycle && cycle.status !== 0) {
                console.log('цикл больше неактивен');
                useDisconnect();
                clearTvOrders();
                stats.currentCycle = null;
            } else if (orders && currentCyclePair.value) {
                usePrepareTvOrders({
                    cycle,
                    orders,
                    pair: currentCyclePair.value,
                    algo: botShortInfo.value?.algo,
                });
            }
        };

        useConnect({
            botId,
            cycleId,
            callBack,
        });
    };

    watch(() => filters.page, v => {
        stats.currentCycle = null;
    });

    const insuranceOrderShow = computed(() => {
        return botShortInfo.value != null && !botShortInfo.value?.simulate && !!activeCycle.value;
    });

    const statsOrdersDataTablePair = computed(() => stats.ordersDataTable?.records[0].pair.split('/'));

    const buyedPrice = computed(() => {
        const currentCycle = stats.currentCycle;

        const record = stats.cyclesDataTable?.records.find(({ number_id }) => number_id == currentCycle)?.prices;
        
        const price = record != 'NA' ? record.split('\n')[2] : null;

        return price != null ? parseFloat(Number(price).toFixed(6)) : '---';
    });

    const buyedPriceAll = computed(() => {
        const list =
            stats.ordersDataTable?.records
                .filter(({ status, order_type }) => status == 1 && order_type == (isLong.value ? 'buy' : 'sell'))
            || [];

        const result = list.reduce((accum, value) => accum + (value.rate * value.amount_processed), 0);

        return parseFloat(result.toFixed(6));
    });

    const buyedVolume = computed(() => {
        const list =
            stats.ordersDataTable?.records
                .filter(({ status, order_type }) => status == 1 && order_type == (isLong.value ? 'buy' : 'sell'))
            || [];

        const result = list.reduce((accum, value) => accum + +value.amount_processed, 0);

        return parseFloat(result.toFixed(6));
    });

    const averagePrice = computed(() => {
        return parseFloat((buyedPriceAll.value / buyedVolume.value).toFixed(6));
    });

    const buyedVolumeLabel = computed(() => isLong.value
        ? (stats.localization?.coins_purchased || 'Куплено монет')
        : (stats.localization?.coins_sold || 'Продано монет'),
    );

    const optionsStatuses = computed(() => {
        const arr = stats.cycleFilters.map(({ id, title }) => ({
            value: id,
            label: title,
        })).sort(({ value }, { value: value2 }) => value - value2);

        arr.unshift({
            value: -1,
            label: stats.localization['trade_statistics_filters_bots_all_button'],
        });

        return arr;
    });

    const ordersFilters = reactive(CRUD_PAGER_FACTORY({
        bots: [+route.params.id],
    }, {
        perPage: '*',
    }));

    const periods = computed(() => {
        const arr = filters.filters.period.split(' ');

        return [new Date(arr[0]).getTime(), new Date(arr[1]).getTime()];
    });

    const buttons = computed(() => [
        {
            show: true,
            loading: false,
            icon: Play20Regular,
            label: stats.localization['trade_statistics_management_start_button'],
            fn() {
                canDoAction('start');
            },
        }, {
            show: !botShortInfo.value?.simulate,
            loading: false,
            icon: PauseCircle24Regular,
            label: stats.localization['trade_statistics_management_pause_button'],
            fn() {
                canDoAction('pause');
            },
        }, {
            show: true,
            loading: false,
            icon: Stop20Regular,
            label: stats.localization['trade_statistics_management_stop_button'],
            fn() {
                canDoAction('stop');
            },
        }, {
            show: true,
            loading: false,
            icon: ArrowSync12Regular,
            label: stats.localization['trade_statistics_management_restart_button'],
            fn() {
                canDoAction('restart');
            },
        }, {
            show: true,
            loading: false,
            icon: Edit20Filled,
            label: stats.localization['trade_statistics_management_edit_button'],
            fn() {
                router.push({ name: 'bots.edit', params: { id: route.params.id } });
            },
        }, {
            show: !botShortInfo.value?.simulate,
            loading: false,
            label: stats.localization['trade_statistics_management_cancelorders_button'],
            fn() {
                confirm.openConfirm({
                    disabledByChecked: false,
                    confirmLabel: stats.localization['trade_statistics__stop_bot_after_operation'],
                    declineMessage: stats.localization['trade_statistics_management_cancelorders_button'],
                    fn: $event => onCancelOrders($event),
                    type: 'confirm_check',
                });
            },
        },{
            show: !botShortInfo.value?.simulate,
            loading: false,
            label: stats.localization['trade_statistics_management_markcyclecanceled_button'],
            fn() {
                markLastAsCanceled.value = true;
            },
        }, {
            show: botShortInfo.value?.simulate,
            loading: loadings.clearCycles,
            label: stats.localization['trade_statistics_bots_table_actions_clear'],
            fn() {
                clearCycles(route.params.id);
            },
        }, {
            show: true,
            loading: loadings.clearCycles,
            label: stats.localization['trade_statistics_bots_table_mass_force_stop'],
            fn() {
                canDoAction('force_stop');
            },
        },
    ]);

    const dashProfit = computed(() => (
        {
            title: stats.localization['trade_statistics_total_profit'],
            data: stats.cyclesDataTable?.revenue_stats,
            ciclos: stats.cyclesDataTable?.revenue_stats?.cycles.completed,
            stats: !stats.cyclesDataTable?.revenue_stats ? [] : (Object.entries(stats.cyclesDataTable?.revenue_stats.coins).map(([key, value]) => [...value, key]))
                .sort(([ , , a], [ , , b]) => {
                    if (a < b)
                        return -1;
                    if (a > b)
                        return 1;
                    return;
                }),
        }),
    );

    const columns = computed(() => {
        if (stats.cyclesDataTable?.columns) {
            const arr = [...stats.cyclesDataTable.columns].filter(({ title }) => title);
            return arr;
        } else return [];
    });

    const columnsOrdersTable = computed(() => {
        if (stats.cyclesDataTable?.columns) {
            const arr = [...stats.ordersDataTable.columns].filter(({ title }) => title);
            return arr;
        } else return [];
    });

    watch(
        () => stats.currentCycle,
        async (v, oldValue) => {
            oldCurrentCycle.value = oldValue;

            // if (v == null) {
            //     if (intervalId.value == null) {
            //         intervalId.value = setInterval(() => {
            //             if (stats.currentCycle != null) {
            //                 clearInterval(intervalId.value);
            //                 intervalId.value = null;
            //                 return;
            //             }
            //             cyclesGet({
            //                 showLoading: false,
            //                 resetOrdersDataTable: false,
            //             });
            //         }, 15000);
            //     }
            // } else {
            //     if (intervalId.value != null) {
            //         clearInterval(intervalId.value);
            //         intervalId.value = null;
            //     }
            // }
        },
    );

    watch(() => stats.currentCycle, async () => {
        if (init.value) {
            // if (stats.currentCycle) {
            //     resetOrdersFilters();
            //     ordersGet({ showLoading: true });
            // }
        } else {
            usePrepareTvOrders({
                pair: currentCyclePair.value,
                algo: botShortInfo.value?.algo,
            });
        }
    });

    watch(() => stats.forAllCycles, () => {
        if (stats.currentCycle && init.value) {
            resetOrdersFilters();
            ordersGet({ showLoading: true });
        }
    });

    const resetOrdersFilters = () => {
        ordersFilters.page = 1;
        // ordersFilters.perPage = 50;
    };

    const getShortInfo = async (showLoading = true) => {
        if (showLoading)
            gl.showLoading = true;

        try {
            botShortInfo.value = ( await StatsService.getShortInfo(route.params.id) ).data;
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        if (showLoading)
            gl.showLoading = false;
    };

    const changeFilter = ([ key, value ], update = false) => {
        const canUpdate = key === 'query' && !value && stats.cyclesDataTable.pager.query;

        if (key !== 'page') {
            _.set(filters, 'page', 1);
        }

        if (key === 'perPage') {
            LocalStorage.setItem('__filters_cycles_table', value);
        }

        _.set(filters, key, value !== null && value !== undefined ? value : [ -1 ]);
            
        if (update || canUpdate)
            cyclesGet();
    };

    const changeFilter2 = ([ key, value ], update = false) => {
        const canUpdate = key === 'query' && !value && stats.cyclesDataTable.pager.query;

        if (key !== 'page') {
            _.set(ordersFilters, 'page', 1);
        }

        _.set(ordersFilters, key, value !== null && value !== undefined ? value : [ -1 ]);
            
        if (update || canUpdate) {
            ordersGet();
        }
    };

    const changeOrdersFilters = ([ key, value ], update = true) => {
        if (key === 'perPage') {
            _.set(ordersFilters, 'page', 1);
        };

        _.set(ordersFilters, key, value !== null && value !== undefined ? value : [ -1 ]);
            
        if (update) {
            ordersGet();
        }
    };

    const canDoAction = async curAction => {
        gl.showLoading = true;

        confirmLabel.value = null;
        declineMessage.value = null;

        if (curAction === 'start') {
            try {
                const result = await BotsService.canDoAction({
                    id: route.params.id,
                    action: curAction,
                });

                if (result) {
                    if (result.data.confirm_label && result.data.decline_message) {
                        confirm.openConfirm({
                            confirmLabel: result.data.confirm_label,
                            declineMessage: result.data.decline_message,
                            fn: () => onGeneralConfirmClicked(curAction),
                            type: 'confirm_check',
                        });
                    } else {
                        confirm.openConfirm({
                            title: refs.localization.confirmations.bot[curAction],
                            fn: () => onGeneralConfirmClicked(curAction),
                        });
                    }

                    if (result?.postMessages) {
                        result.postMessages.forEach(el => {
                            notification[el.success ? 'success' : 'error']({
                                content: el.msg,
                                duration: 2500,
                                keepAliveOnHover: true,
                            });
                        });
                    }
                }
            } catch {
                notification.error({
                    content: t('errorMessage'),
                    duration: 2500,
                    keepAliveOnHover: true,
                });
            };
        }  else {
            confirm.openConfirm({
                title: refs.localization.confirmations.bot[curAction],
                fn: () => onGeneralConfirmClicked(curAction),
            });
        }

        gl.showLoading = false;
    };

    const onGeneralConfirmClicked = async curAction => {
        gl.showLoading = true;

        try {
            let result;

            if (curAction === 'delete') {
                result = await BotsService.delete({
                    ids: [route.params.id],
                });
            } else {
                result = await BotsService.action({
                    id: route.params.id,
                    action: curAction,
                });
            }
                
            if (result) {
                // show messages
                if (result.postMessages)
                    result.postMessages.forEach(el => {
                        gl.showNotification({
                            type: el.success ? 'success' : 'error',
                            msg: el.msg,
                        });
                    });

                // if (data.data.status) {
                //     return void emit('getData');
                // }
                await getAll({ showLoading: false });
            }
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        gl.showLoading = false;
    };

    const updateDate = $event => {
        if ($event) 
            filters.filters.period = `${new Date($event[0]).toISOString().split('T')[0]} ${new Date($event[1]).toISOString().split('T')[0]}`;
    };

    const onCancelOrders = async (obj) => {
        gl.showLoading = true;

        try {
            let prepare;

            prepare = await StatsService.cancelOrders({
                id: route.params.id,
                stop_bot_after_operation: obj.genConfCheck,
            });
                
            if (prepare) {
                // show messages
                if (!prepare.data.status) {
                    prepare.data.errors.forEach(({ msg }) => {
                        notification.error({
                            content: msg,
                            duration: 2500,
                            keepAliveOnHover: true,
                        });
                    });
                        
                } else {
                    return void getAll();
                }
            }
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        gl.showLoading = false; 
    };

    const doMarkLastAsCanceled = async () => {
        gl.showLoading = true;

        try {
            const prepare = await StatsService.markLastAsCanceled(route.params.id);

            // show messages
            prepare.postMessages.forEach(el => {
                notification[el.success ? 'success' : 'error']({
                    content: el.msg,
                    duration: 2500,
                    keepAliveOnHover: true,
                });
            });
            
            if (prepare.data.status) {
                markLastAsCanceled.value = false;
                await getShortInfo();
                cyclesGet();

                return;
            }
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        gl.showLoading = false;
    };

    const renderDefaultCycle = async ({records}) => {
        const activeCycleIdx = records.findIndex(({ status }) => status == 0);
        const firstOrderIsDone = records[0].status == 1;

        if (defaultCycleId.value) {
            // цикл по умолчанию /?cycle_id
            stats.currentCycle = defaultCycleId.value;

            // получение ордеров до иницализации
            ordersGet({ showLoading: true, autoScroll: false });

            defaultCycleId.value = null;
        }
        else if (~activeCycleIdx) {
            // активный цикл
            const item = records[activeCycleIdx];
            stats.currentCycle = item.number_id;

            // // получение ордеров до иницализации
            // ordersGet({ showLoading: true, autoScroll: false });
        }
        // else if (firstOrderIsDone) {
        //     // выполненный цикл
        //     stats.currentCycle = records[0].number_id;

        //     // получение ордеров до иницализации
        //     ordersGet({ showLoading: true, autoScroll: false });
        // }
        else {
            // записываем текущую пару цикла
            chartModel.value.pair = botShortInfo.value?.tickers_for;
        }
    };

    const cyclesGet = async ({
        showLoading = true,
        resetOrdersDataTable = true,
        doRenderDefaultCycle = true } = {}) => {
        if (showLoading)
            gl.showLoading = true;

        loadings.table = true;

        if (resetOrdersDataTable)
            stats.ordersDataTable = null;

        try {
            const prepare = await StatsService.cyclesGet({
                pager: filters,
            });
            
            const i = prepare.data.columns.findIndex(({ name }) => name == 'current_orders_info');

            if (~i) {
                prepare.data.columns.splice(i, 0, {
                    isSortable: true,
                    name: 'p_n_l',
                    title: 'PNL',
                });
            }

            if (Array.isArray(prepare.data.records)) {
                let activeIdx;
                if (!prepare.data.records.length) {
                    // записываем текущую пару цикла
                    pairOFCurrentCycle.value = botShortInfo.value?.tickers_for;
                }

                prepare.data.records.forEach((item, i) => {
                    const price = item.prices;
                    item.bot_id = botShortInfo.value.id;
                    item.exchange = botShortInfo.value.exchange;
                    item.cycle_id = item.id;
                    item.algo = botShortInfo.value.algo;

                    // wsOrdersConnect для активного цикла
                    if (item.status == 0) {
                        activeIdx = i;
                    }

                    if (price == null || price.includes('NA')) {
                        return;
                    } else {
                        // calc pbl
                        const {
                            newText,
                            zeroPrice,
                            fixPrice,
                            zeroDelta,
                            course,
                        } = usePNL({ price, algo: botShortInfo.value?.algo });
                        
                        prepare.data.records[i].prices_draft = newText;
                        item.r1 = zeroPrice;
                        item.r2 = fixPrice;
                        item.p_n_l = (((zeroDelta / course) * 100).toFixed(2)) * -1;
                    }
                });
                
                if (stats.cyclesDataTable) {
                    Object.keys(prepare.data).forEach(key => {
                        if (!['stats', 'columns'].includes(key)) {
                            stats.cyclesDataTable[key] = prepare.data[key];
                        }
                    });
                } else {
                    stats.cyclesDataTable = prepare.data;
                }
    
                stats.cyclesDataTable.records.push({
                    type: 'footer',
                    ...stats.cyclesDataTable.totals,
                });

                if (activeIdx !== undefined) {
                    wsOrdersConnect(activeIdx);
                }

                if (doRenderDefaultCycle) {
                    await renderDefaultCycle({records: stats.cyclesDataTable.records});
                }
            }
        } catch (e) {
            console.log(e);
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        if (showLoading)
            gl.showLoading = false;
    
        loadings.table = false;
            
    };

    const ordersGet = async ({
        showLoading = false,
        order_id = null,
        autoScroll = true,
        prepareTvOrders = true,
    } = {}) => {
        if (showLoading)
            gl.showLoading = true;

        loadings.tableOrders = true;

        ordersFilters.filters.cycles = [ +order_id || (stats.forAllCycles ? -1 : stats.currentCycle)];
            
        try {
            const prepare = await StatsService.ordersGet({
                pager: ordersFilters,
            });

            prepare.data.records
                .sort((a, b) => {
                    const f = a.cycle_id__order_id.split('/')[1];
                    const s = b.cycle_id__order_id.split('/')[1];

                    return s - f;
                })
                .sort((a, b) => {
                    const f = a.order_type == 'buy' ? 1 : 0;
                    const s = b.order_type == 'buy' ? 1 : 0;

                    return botShortInfo.value.algo == 'long' ? f - s : s - f;
                });

            if (stats.ordersDataTable) {
                Object.keys(prepare.data).forEach(key => {
                    if (!['columns'].includes(key)) {
                        stats.ordersDataTable[key] = prepare.data[key];
                    }
                });
            } else {
                stats.ordersDataTable = prepare.data;
            }

            // записываем текущую пару цикла
            pairOFCurrentCycle.value = prepare.data.records[0].pair;

            // подготавливаем ордера для графика
            prepare.data.records = prepare.data.records.map(el => {
                return {
                    type: el.order_type,
                    amount: +el.first_coin__first_coin_comm.split('/')[0],
                    ts_end: el.date_end,
                    ...el,
                };
            });

            if (prepareTvOrders) {
                usePrepareTvOrders({
                    cycle: stats.currentCycle,
                    orders: prepare.data.records,
                    pair: currentCyclePair.value,
                    algo: botShortInfo.value?.algo,
                });
            }

            if (autoScroll) {
                setTimeout(() => {
                    const el = document.querySelector('#cycles-orders-table');
                    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
                });
            }
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        loadings.tableOrders = false;

        if (showLoading)
            gl.showLoading = false;
    };

    const getData = async () => {
        try {
            if (!stats.refs)
                stats.refs = await StatsService.getRefs();
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };
    };

    const currency = icon => refs.currencies.find(el => el.id === icon);
    const exchange = exchange => refs.exchanges.find(el => el.id === exchange);

    const getAll = async ({
        showLoading = true,
    } = {}) => {
        if (showLoading)
            gl.showLoading = true;

        loadings.table = true;

        stats.currentCycle = null;

        await Promise.allSettled([
            getData(),
            await getShortInfo(false),
            cyclesGet(),
        ]);

        init.value = true;

        if(showLoading)
            gl.showLoading = false;

        loadings.table = false;
    };

    const totalUpdate = async () => {
        totalUpdateLoading.value = true;

        await Promise.allSettled([
            getData(),
            await getShortInfo(false),
            cyclesGet({ showLoading: false, resetOrdersDataTable: false }),
        ]);

        totalUpdateLoading.value = false;
    };

    const sortColumn = $event => {
        _.set(
            filters,
            'sort_column',
            $event.columnKey !== undefined && $event.order !== undefined
                ? $event.columnKey
                : '');

        _.set(
            filters,
            'sort_dir',
            $event.order !== undefined
                ? $event.order
                : '');

        cyclesGet();
    };

    const downloadData = async format => {
        gl.showLoading = true;

        try {
            const { redirectTo } = ( await StatsService.cyclesGet({
                format,
                pager: filters,
            }) ).data;

            window.location.href = redirectTo;
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        gl.showLoading = false;
    };

    const downloadData2 = async format => {
        gl.showLoading = true;

        try {
            const { redirectTo } = ( await StatsService.ordersGet({
                format,
                pager: ordersFilters,
            }) ).data;

            window.location.href = redirectTo;
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        gl.showLoading = false;
    };

    const doSomething = ([ action, value ]) => {
        const fns = {
            setCurrentCycle: () => {
                if (value.status == 0) {
                    const idx = stats.cyclesDataTable?.records.findIndex(({ number_id }) => {
                        return number_id == value.number_id;
                    });
                    wsOrdersConnect(idx);
                } else {
                    useDisconnect();
                }
                
                if (stats.currentCycle == value.number_id && !stats.ordersDataTable) {
                    resetOrdersFilters();
                    ordersGet({ showLoading: true, prepareTvOrders: false });
                } else {
                    stats.currentCycle = value.number_id;
                    ordersGet({ showLoading: true });
                }
            },
        };

        fns[action]();
    };

    const clearCycles = async id => {
        loadings.clearCycles = true;

        try {
            const result = await StatsService.clearCycles(id);

            // show messages
            result.postMessages.forEach(el => {
                notification[el.success ? 'success' : 'error']({
                    content: el.msg,
                    duration: 2500,
                    keepAliveOnHover: true,
                });
            });
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        loadings.clearCycles = false;
    };

    const closeAtMarketConfirm = () => {
        confirm.openConfirm({
            disabledByChecked: false,
            declineMessage: refs.localization.confirmations.bot?.close_at_market || 'Закрыть цикл по рынку?',
            confirmLabel: stats.localization['trade_statistics__stop_bot_after_operation'],
            type: 'confirm_check',
            fn: $event => closeAtMarket($event),
        });
    };

    const closeAtMarket = async obj => {
        loadings.closeAtMarket = true;

        const payload = {
            _formPath: 'closeAtMarket',
            id: route.params.id,
            market: true,
            stop_bot_after_fix_completed: obj.genConfCheck };

        try {
            const prepare = await StatsService.resetFixOrder(payload);

            if (prepare) {
            // show messages
                if (prepare.postMessages)
                    prepare.postMessages.forEach(el => {
                        gl.showNotification({
                            type: el.success ? 'success' : 'error',
                            msg: el.msg,
                        });
                    });
              
                // show messages
                if (prepare.data.errors)
                    prepare.data.errors.forEach(el => {
                        gl.showNotification({
                            type: 'error',
                            msg: el.msg,
                        });
                    });
            }

            if (prepare.data.status) {
                clearAllTvOrders();
                useDisconnect();

                stats.currentCycle = null;

                // setTimeout(async () => {
                //     if (stats.currentCycle == null) {
                //         await ordersGet({ id: route.params.id, autoScroll: false });
                //     }
                // }, 30000);
            } else {
                loadings.closeAtMarket = false;
            }
          
        } catch (error) {
            console.log(error);

            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        loadings.closeAtMarket = false;
    };

    const LastCyclCallBack = async (msg) => {
        if (msg.channel !== 'last_cycle') return;
        if (String(msg.bot_id) !== String(route.params.id)) return;

        if (lastCycleInFlight.value) return;
        lastCycleInFlight.value = true;

        try {
            const table = stats.cyclesDataTable;
            if (!table?.records?.length) return;
            if (!botShortInfo.value) return;

            if (stats.currentCycle != null) return;
            if (msg.cycle.status != 0) return;

            const top = table.records[0];
            if (msg.cycle.id == top.id) return;

            const ids = [String(top.number_id), String(msg.cycle.id)];

            for (let idx = 0; idx < ids.length; idx++) {
                const localFilter = CRUD_PAGER_FACTORY(
                    { bots: [+route.params.id], statuses: [-1] },
                    { page: 1, perPage: 10, query: ids[idx] },
                );

                const resp = await StatsService.cyclesGet({ pager: localFilter });
                const cycle = resp?.data?.records?.[0];
                if (!cycle) continue;

                const price = cycle.prices;
                cycle.bot_id = botShortInfo.value.id;
                cycle.exchange = botShortInfo.value.exchange;
                cycle.cycle_id = cycle.id;
                cycle.algo = botShortInfo.value.algo;

                if (typeof price === 'string' && !price.includes('NA')) {
                    const { newText, zeroPrice, fixPrice, zeroDelta, course } =
          usePNL({ price, algo: botShortInfo.value?.algo });

                    cycle.prices_draft = newText;
                    cycle.r1 = zeroPrice;
                    cycle.r2 = fixPrice;
                    cycle.p_n_l = Number((((zeroDelta / course) * 100).toFixed(2))) * -1;
                }

                if (idx === 0) {
                    table.records[0] = cycle;
                } else {
                    table.records.unshift(cycle);
                    stats.currentCycle = table.records[0].number_id;
                    wsOrdersConnect(0);
                }
            }
        } finally {
            lastCycleInFlight.value = false;
        }
    };
    
    onMounted(async () => {
        await getAll();

        useWsLastCycleConnect({ botId: route.params.id, callBack: LastCyclCallBack });

        init.value = true;
    });

    onUnmounted(() => {
        stats.currentCycle = null;
        stats.cyclesDataTable = null;
        stats.ordersDataTable = null;

        // отключаемся от wsOrders
        useDisconnect();
        useWsLastCycleDisconnect();
    });

    return {
        gl,
        env,
        init,
        refs,
        stats,
        isLong,
        buttons,
        periods,
        filters,
        columns,
        loadings,
        chartModel,
        activeCycle,
        buyedVolume,
        buyedPrice,
        refFilters,
        dashProfit,
        botShortInfo,
        confirmLabel,
        genConfCheck,
        averagePrice,
        reSetFixOrder,
        buyedPriceAll,
        declineMessage,
        ordersFilters,
        filtersChanged,
        buyedVolumeLabel,
        totalUpdateLoading,
        markLastAsCanceled,
        columnsOrdersTable,
        insuranceOrderShow,
        statsOrdersDataTablePair,
        numberOfDecimalPlaces,
        router,
        stateText,
        showMoreButtons,
        currentCycleInfo,
        pairOFCurrentCycle,
        t,
        getAll,
        currency,
        exchange,
        cyclesGet,
        ordersGet,
        updateDate,
        sortColumn,
        totalUpdate,
        doSomething,
        downloadData,
        resetFilters,
        changeFilter,
        getShortInfo,
        onCancelOrders,
        changeFilter2,
        downloadData2,
        closeAtMarketConfirm,
        changeOrdersFilters,
        doMarkLastAsCanceled,
        onGeneralConfirmClicked,
    };
}